import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  
  // Check if current page is Privacy Policy
  const isPrivacyPolicyPage = location.pathname === '/privacy-policy' || location.pathname.includes('privacy');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Courses', path: '/courses' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Main Header */}
    <motion.header
        className={`fixed w-full z-50 transition-all duration-500 ${
          isScrolled 
            ? 'border-b border-gray-100 shadow-xl backdrop-blur-lg dark:border-gray-800 bg-white/95 dark:bg-slate-900/95' 
            : isPrivacyPolicyPage
            ? 'border-b shadow-xl backdrop-blur-lg bg-white/80 dark:bg-slate-900/80 border-white/90 dark:border-gray-800/90'
            : 'bg-transparent'
        }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
    >
        <div className="container overflow-hidden px-4 mx-auto max-w-full sm:px-2 lg:px-5">
          <div className="flex justify-between items-center w-full min-w-0 h-20">
        {/* Logo */}
        <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center min-w-0 flex-shrink-0 max-w-[70%]"
            >
              <Link to="/" className="flex items-center -space-x-2 min-w-0 sm:space-x-1">
                <div className="relative">
                  <img
                    src="/assests/logo.png"
                    alt="Skills Earth Academy"
                    className="w-auto h-16 transition-all duration-300"
                  />
                  {!isScrolled && (
                    <motion.div
                      className="absolute -inset-2 bg-gradient-to-r rounded-full blur-lg from-indigo-400/20 to-blue-400/20"
                      animate={{ 
                        scale: [1, 1.1, 1],
                        opacity: [0.3, 0.5, 0.3]
                      }}
                      transition={{ 
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  )}
                </div>
                <div className="block flex-1 min-w-0">
                  <h1 className={`font-black transition-all duration-300 text-[13px] sm:text-[24px] ${
                    isScrolled 
                      ? 'text-gray-1200 dark:text-gray-100' 
                      : isPrivacyPolicyPage 
                      ? 'drop-shadow-lg text-gray-1200 dark:text-gray-100'
                      : 'text-gray-1200 dark:text-gray-100'
                  } tracking-tight truncate leading-tight`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Skills Earth Academy
                  </h1>
                  <p className={`font-medium tracking-wide transition-all duration-300 text-[9px] sm:text-[14px] ${
                    isScrolled 
                      ? 'text-gray-500 dark:text-gray-300' 
                      : isPrivacyPolicyPage 
                      ? 'text-gray-500 drop-shadow-md dark:text-gray-300'
                      : 'text-gray-500 dark:text-gray-300'
                  }`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Empowering Your Future
                  </p>
                </div>
              </Link>
        </motion.div>

        {/* Desktop Navigation */}
            <nav className="hidden flex-shrink-0 items-center space-x-1 lg:flex">
              {navItems.map((item, index) => (
            <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
            >
              <Link
                    to={item.path}
                    className={`relative px-4 py-2 rounded-lg font-medium transition-all duration-300 group ${
                      isActive(item.path)
                        ? isScrolled 
                          ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                          : isPrivacyPolicyPage
                          ? 'text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 drop-shadow-lg'
                          : 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                        : isScrolled
                        ? 'text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                        : isPrivacyPolicyPage
                        ? 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 drop-shadow-md'
                        : 'text-gray-800 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {item.name}
                    {isActive(item.path) && (
                      <motion.div
                        className={`absolute right-0 bottom-0 left-0 h-0.5 ${
                          isScrolled 
                            ? 'bg-indigo-600 dark:bg-indigo-400' 
                            : isPrivacyPolicyPage 
                            ? 'bg-gray-600 dark:bg-gray-400'
                            : 'bg-indigo-600 dark:bg-indigo-400'
                        }`}
                        layoutId="activeTab"
                        transition={{ duration: 0.3 }}
                      />
                    )}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r rounded-lg opacity-0 transition-opacity duration-300 from-indigo-500/10 to-blue-500/10 group-hover:opacity-100"
                      whileHover={{ scale: 1.05 }}
                    />
              </Link>
            </motion.div>
          ))}
        </nav>

        {/* Theme Toggle Button */}
        <div className="hidden flex-shrink-0 items-center lg:flex">
          <ThemeToggle />
        </div>

        {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-1.5 sm:p-2 rounded-lg transition-colors duration-300 lg:hidden shadow-lg flex-shrink-0 ml-2 ${
                isScrolled 
                  ? 'text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80' 
                  : isPrivacyPolicyPage 
                  ? 'text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80'
                  : 'text-gray-900 dark:text-gray-100 bg-white/90 dark:bg-gray-800/90'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className={`w-5 h-5 sm:w-6 sm:h-6 ${
                      isScrolled 
                        ? 'text-gray-700 dark:text-gray-300' 
                        : isPrivacyPolicyPage 
                        ? 'text-gray-700 dark:text-gray-300'
                        : 'text-gray-900 dark:text-gray-100'
                    }`} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className={`w-5 h-5 sm:w-6 sm:h-6 ${
                      isScrolled 
                        ? 'text-gray-700 dark:text-gray-300' 
                        : isPrivacyPolicyPage 
                        ? 'text-gray-700 dark:text-gray-300'
                        : 'text-gray-900 dark:text-gray-100'
                    }`} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
        <AnimatePresence>
      {isMenuOpen && (
        <motion.div
              className="border-t border-gray-100 backdrop-blur-lg dark:border-gray-800 lg:hidden bg-white/95 dark:bg-slate-900/95"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className="container px-4 py-6 mx-auto">
                <div className="space-y-2">
                  {navItems.map((item, index) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
              <Link
                        to={item.path}
                        className={`block px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                          isActive(item.path)
                            ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 border-l-4 border-indigo-600 dark:border-indigo-400'
                            : 'text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                onClick={() => setIsMenuOpen(false)}
              >
                        {item.name}
              </Link>
                    </motion.div>
                  ))}
                </div>
                
                {/* Mobile Theme Toggle */}
                <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-center">
                    <ThemeToggle />
                  </div>
                </div>

          </div>
        </motion.div>
      )}
        </AnimatePresence>
    </motion.header>
    </>
  );
};

export default Header;